import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Volume2, VolumeX, RotateCcw } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { BotanicalRise } from "./Botanical";
import { ABOUT } from "../data/content";

const MISSION_VIDEO_ID = "IqvVV30qbog";

// Solid brand glyphs (fill currentColor) so the social icons read as solid marks.
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z" />
  </svg>
);

const LinkedinIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
  </svg>
);

const YoutubeIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

// YouTube-backed film styled like the Event Details intro film: autoplay, muted,
// looping, YouTube chrome cropped/hidden, with a custom "Tap for sound" toggle.
const MissionFilm = () => {
  const playerRef = useRef(null);
  const hostRef = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const init = () => {
      if (cancelled || !hostRef.current || !window.YT?.Player) return;
      playerRef.current = new window.YT.Player(hostRef.current, {
        videoId: MISSION_VIDEO_ID,
        width: "100%",
        height: "100%",
        playerVars: {
          autoplay: 1, mute: 1, controls: 0, modestbranding: 1, rel: 0,
          playsinline: 1, disablekb: 1, fs: 0, iv_load_policy: 3, cc_load_policy: 0,
        },
        events: {
          onReady: (e) => { e.target.mute(); e.target.playVideo(); },
          onStateChange: (e) => {
            // Loop without the playlist param (which some videos block)
            if (e.data === window.YT.PlayerState.ENDED) {
              e.target.seekTo(0);
              e.target.playVideo();
            }
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      init();
    } else {
      if (!document.getElementById("youtube-iframe-api")) {
        const tag = document.createElement("script");
        tag.id = "youtube-iframe-api";
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { if (prev) prev(); init(); };
    }
    return () => { cancelled = true; try { playerRef.current?.destroy(); } catch { /* player already destroyed */ } };
  }, []);

  const toggleSound = () => {
    const p = playerRef.current;
    if (!p) return;
    if (muted) { p.unMute(); p.setVolume(100); p.playVideo(); setMuted(false); }
    else { p.mute(); setMuted(true); }
  };

  const restart = () => {
    const p = playerRef.current;
    if (!p) return;
    p.seekTo(0);
    p.playVideo();
  };

  return (
    <div className="relative mx-auto w-full max-w-4xl" data-testid="about-mission-film">
      {/* Gilded frame */}
      <div className="rounded-xl bg-gradient-to-br from-mds-gold/70 via-mds-gold/30 to-mds-gold/70 p-[2px] shadow-[0_28px_70px_-25px_rgba(20,25,45,0.55)]">
        <div className="group relative overflow-hidden rounded-[10px] bg-mds-navy">
          <div className="relative aspect-video w-full overflow-hidden">
            {/* Oversized player crops YouTube's title/logo bars out of view */}
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[300%] w-full -translate-x-1/2 -translate-y-1/2">
              <div ref={hostRef} className="h-full w-full" />
            </div>
          </div>
          {/* Transparent guard blocks clicks so YouTube end-screens/controls never appear */}
          <div className="absolute inset-0" aria-hidden="true" />
          {/* Inset hairline + soft vignette */}
          <div className="pointer-events-none absolute inset-0 rounded-[10px] ring-1 ring-inset ring-white/10" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/55 to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent" />

          {/* Restart */}
          <button
            type="button"
            onClick={restart}
            aria-label="Restart video"
            className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-black/45 text-mds-cream backdrop-blur-md transition-colors duration-300 hover:border-mds-gold hover:text-mds-gold lg:h-9 lg:w-9"
            data-testid="about-mission-restart"
          >
            <RotateCcw className="h-4 w-4" strokeWidth={1.6} />
          </button>

          {/* Sound toggle */}
          <button
            type="button"
            onClick={toggleSound}
            aria-label={muted ? "Unmute video" : "Mute video"}
            className={`absolute bottom-4 left-1/2 z-10 flex min-h-[44px] -translate-x-1/2 items-center gap-2 rounded-full border px-4 py-2 font-sans text-[11px] uppercase tracking-[0.16em] backdrop-blur-md transition-colors duration-300 lg:min-h-0 ${
              muted
                ? "sound-cta-pulse border-mds-gold bg-black/55 text-mds-gold"
                : "border-white/25 bg-black/40 text-mds-cream hover:border-mds-gold hover:text-mds-gold"
            }`}
            data-testid="about-mission-sound-toggle"
          >
            {muted ? <VolumeX className="h-4 w-4" strokeWidth={1.4} /> : <Volume2 className="h-4 w-4" strokeWidth={1.4} />}
            {muted ? "Tap for sound" : "Sound on"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const About = () => {
  return (
    <section id="about" className="relative overflow-hidden bg-mds-cream" data-testid="about-section">
      <div className="mx-auto max-w-[1200px] px-5 pb-14 pt-24 sm:px-8 sm:pb-16 sm:pt-32">
        <Reveal className="text-center">
          <p className="mt-2 pb-6 pt-4 font-script text-6xl leading-[1.3] text-gold sm:text-8xl">Drive Hope Forward</p>
          <h2 className="mt-1 flex items-center justify-center gap-2 font-display smallcaps text-3xl font-medium tracking-tight text-mds-ink sm:text-5xl">
            <span className="uppercase text-lg sm:text-2xl">With</span> {ABOUT.title}
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <p className="mt-5 font-display text-2xl italic leading-snug text-mds-ink sm:text-3xl">{ABOUT.lead}</p>
            <div className="mt-6 space-y-5">
              {ABOUT.paragraphs.map((p, i) => (
                <p key={i} className="font-body text-base leading-relaxed text-mds-inksoft">
                  {p}
                </p>
              ))}
            </div>
          </Reveal>

          <Reveal className="lg:col-span-6" delay={0.12}>
            <p className="subhead text-[12px]">What Your Generosity Makes Possible</p>
            <ul className="mt-6 space-y-4">
              {ABOUT.makesPossible.map((m, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="flex items-start gap-4 border-b border-mds-line pb-4"
                >
                  <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-mds-green/10 text-mds-green">
                    <Check size={14} strokeWidth={2.5} />
                  </span>
                  <span className="font-body text-base leading-relaxed text-mds-ink">{m}</span>
                </motion.li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal className="mt-16" delay={0.1}>
          <div className="mx-auto max-w-4xl">
            <p className="text-center font-display smallcaps text-2xl text-mds-ink sm:text-3xl">See Our Mission in Motion</p>
            <div className="mt-6">
              <MissionFilm />
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-16" delay={0.08}>
          <div className="mx-auto max-w-3xl text-center" data-testid="social-connect">
            <p className="font-display smallcaps text-xl leading-snug text-mds-ink sm:text-2xl">
              Connect with Naples Cancer Advisors on Social Media for Updates
            </p>
            <div className="mt-7 flex items-center justify-center gap-5">
              <a
                href="https://www.facebook.com/naplescanceradvisors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Naples Cancer Advisors on Facebook"
                data-testid="social-facebook"
                className="flex h-12 w-12 items-center justify-center rounded-full border border-mds-gold/50 text-mds-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-mds-gold hover:bg-mds-gold hover:text-mds-cream"
              >
                <FacebookIcon className="h-5 w-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/naples-cancer-advisors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Naples Cancer Advisors on LinkedIn"
                data-testid="social-linkedin"
                className="flex h-12 w-12 items-center justify-center rounded-full border border-mds-gold/50 text-mds-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-mds-gold hover:bg-mds-gold hover:text-mds-cream"
              >
                <LinkedinIcon className="h-5 w-5" />
              </a>
              <a
                href="https://www.youtube.com/channel/UClvgxcNhdgu7Qes7IQvZ4iw"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Naples Cancer Advisors on YouTube"
                data-testid="social-youtube"
                className="flex h-12 w-12 items-center justify-center rounded-full border border-mds-gold/50 text-mds-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-mds-gold hover:bg-mds-gold hover:text-mds-cream"
              >
                <YoutubeIcon className="h-5 w-5" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>

      <BotanicalRise className="w-full" />
    </section>
  );
};
